import JSZip from 'jszip';
import mime from 'mime';
import React, { useState, useEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import apiConfigs from '../shared/utilities/apiConfigs';
import { api } from '../shared/utilities/api';

// ─── Font injection ────────────────────────────────────────────────────────────
const injectFont = () => {
	if (document.getElementById('get-report-font')) return;
	const link = document.createElement('link');
	link.id = 'get-report-font';
	link.rel = 'stylesheet';
	link.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600&display=swap';
	document.head.appendChild(link);
};

// ─── Inline styles ─────────────────────────────────────────────────────────────
const injectStyles = () => {
	if (document.getElementById('get-report-styles')) return;
	const style = document.createElement('style');
	style.id = 'get-report-styles';
	style.textContent = `
		.gr-wrap, .gr-wrap *, .gr-wrap *::before, .gr-wrap *::after {
			box-sizing: border-box;
		}
		.gr-wrap {
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
			width: 100%;
			max-width: 100%;
			overflow-x: hidden;
		}

		/* ── Split card — matches header and FileUploader ── */
		.gr-card {
			width: 100%;
			display: grid;
			grid-template-columns: 200px 1fr;
			background: #fff;
			border-radius: 0 0 20px 20px;
			border: 1px solid rgba(0,0,0,0.06);
			border-top: none;
			box-shadow: 0 2px 4px rgba(0,0,0,0.04), 0 20px 48px -20px rgba(0,0,0,0.14);
			overflow: hidden;
		}

		/* ── Left orange panel ── */
		.gr-left {
			background: linear-gradient(155deg, #F4A96A 0%, #E47029 50%, #B94D1A 100%);
			padding: 32px 22px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			position: relative;
			overflow: hidden;
		}
		.gr-left::before {
			content: '';
			position: absolute;
			width: 200px; height: 200px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.14);
			right: -80px; top: -80px;
		}
		.gr-left::after {
			content: '';
			position: absolute;
			width: 140px; height: 140px;
			border-radius: 50%;
			border: 1px solid rgba(255,255,255,0.12);
			left: -50px; bottom: -50px;
		}
		.gr-left__top { position: relative; z-index: 1; }
		.gr-left__icon {
			width: 44px; height: 44px;
			border-radius: 12px;
			background: rgba(255,255,255,0.2);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 14px;
		}
		.gr-left__title {
			font-family: 'Manrope', sans-serif;
			font-size: 16px;
			font-weight: 800;
			color: #fff;
			letter-spacing: -0.01em;
			line-height: 1.3;
			margin-bottom: 8px;
		}
		.gr-left__sub {
			font-size: 12px;
			color: rgba(255,255,255,0.78);
			line-height: 1.55;
		}
		.gr-left__bottom {
			position: relative;
			z-index: 1;
		}
		.gr-left__note {
			display: flex;
			align-items: center;
			gap: 7px;
			font-size: 11px;
			color: rgba(255,255,255,0.7);
		}

		/* ── Right panel ── */
		.gr-right { padding: 28px 24px; min-width: 0; }
		.gr-right__label {
			font-size: 11px;
			font-weight: 700;
			color: #E47029;
			text-transform: uppercase;
			letter-spacing: 0.07em;
			margin-bottom: 6px;
		}
		.gr-right__title {
			font-family: 'Manrope', sans-serif;
			font-size: 18px;
			font-weight: 800;
			color: #18181A;
			letter-spacing: -0.01em;
			margin-bottom: 5px;
		}
		.gr-right__head {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			margin-bottom: 5px;
		}
		.gr-right__sub {
			font-size: 12.5px;
			color: #6B6760;
			line-height: 1.55;
			margin-bottom: 20px;
		}

		/* fields */
		.gr-fields {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 14px;
			margin-bottom: 18px;
		}
		.gr-field label {
			display: block;
			font-size: 11.5px;
			font-weight: 600;
			color: #5B5B60;
			text-transform: uppercase;
			letter-spacing: 0.04em;
			margin-bottom: 7px;
		}
		.gr-field input {
			width: 100%;
			height: 46px;
			padding: 0 14px;
			border: 1.5px solid rgba(0,0,0,0.11);
			border-radius: 10px;
			font-size: 14px;
			font-family: inherit;
			color: #18181A;
			background: #FAFAFA;
			-webkit-appearance: none;
			appearance: none;
			transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
		}
		.gr-field input:hover { border-color: #c2bdb3; }
		.gr-field input:focus {
			outline: none;
			border-color: #E47029;
			box-shadow: 0 0 0 3px rgba(228,112,41,0.12);
			background: #fff;
		}
		.gr-field input:-webkit-autofill,
		.gr-field input:-webkit-autofill:hover,
		.gr-field input:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px #FAFAFA inset;
			-webkit-text-fill-color: #18181A;
		}
		.gr-field input.error { border-color: #E53E3E; }
		.gr-field input.error:focus { box-shadow: 0 0 0 3px rgba(229,62,62,0.12); }
		.gr-field__hint { font-size: 11px; color: #C0BDB8; margin-top: 4px; }
		.gr-field__error { font-size: 11px; color: #E53E3E; margin-top: 4px; }

		/* button */
		.gr-btn {
			width: 100%;
			height: 46px;
			border: none;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			font-family: 'Manrope', sans-serif;
			font-size: 14.5px;
			font-weight: 700;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 8px;
			transition: background 0.15s, transform 0.1s;
			letter-spacing: 0.01em;
		}
		.gr-btn:hover:not(:disabled) { background: #B94D1A; }
		.gr-btn:active:not(:disabled) { transform: scale(0.99); }
		.gr-btn:disabled { background: #E5C9B0; cursor: not-allowed; }

		.gr-spinner-wrap {
			display: flex;
			justify-content: center;
			align-items: center;
			height: 46px;
		}
		.gr-security {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 5px;
			margin-top: 10px;
			font-size: 11px;
			color: #C0BDB8;
		}

		/* exit button */
		.gr-exit {
			display: flex;
			align-items: center;
			gap: 6px;
			padding: 6px 12px;
			border-radius: 8px;
			background: rgba(228,112,41,0.1);
			color: #E47029;
			font-size: 12px;
			font-weight: 700;
			border: none;
			cursor: pointer;
			font-family: inherit;
			flex-shrink: 0;
			transition: background 0.15s;
		}
		.gr-exit:hover { background: rgba(228,112,41,0.18); }

		/* file list state */
		.gr-file-note {
			font-size: 12.5px;
			color: #6B6760;
			line-height: 1.55;
			padding: 11px 14px;
			background: #FEF0E7;
			border: 1px solid #F5D5C0;
			border-radius: 10px;
			margin-bottom: 14px;
		}
		.gr-file-note b { color: #E47029; }

		.gr-dl-all-row {
			display: flex;
			justify-content: flex-end;
			margin-bottom: 12px;
		}
		.gr-dl-all {
			display: flex;
			align-items: center;
			gap: 6px;
			padding: 7px 16px;
			border-radius: 10px;
			background: #E47029;
			color: #fff;
			font-size: 12.5px;
			font-weight: 700;
			border: none;
			cursor: pointer;
			font-family: inherit;
			transition: background 0.15s;
		}
		.gr-dl-all:hover { background: #B94D1A; }

		.gr-file-item {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px 12px;
			border: 1.5px solid rgba(0,0,0,0.08);
			border-radius: 10px;
			margin-bottom: 8px;
			background: #FAFAFA;
			transition: border-color 0.15s;
			min-width: 0;
		}
		.gr-file-item:hover { border-color: #F5D5C0; }
		.gr-file-icon {
			width: 34px; height: 34px;
			border-radius: 9px;
			background: #FEF0E7;
			color: #E47029;
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
		}
		.gr-file-name {
			font-size: 13px;
			font-weight: 500;
			color: #18181A;
			flex: 1;
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.gr-dl-btn {
			padding: 6px 14px;
			border-radius: 8px;
			background: #E47029;
			color: #fff;
			font-size: 12px;
			font-weight: 700;
			border: none;
			cursor: pointer;
			text-decoration: none;
			display: inline-flex;
			align-items: center;
			gap: 5px;
			white-space: nowrap;
			font-family: inherit;
			transition: background 0.15s;
		}
		.gr-dl-btn:hover { background: #B94D1A; }

		/* ── Responsive ── */
		@media (max-width: 680px) {
			.gr-card {
				grid-template-columns: 1fr;
				border-radius: 0 0 16px 16px;
			}
			.gr-left {
				padding: 20px;
				min-height: auto;
			}
			.gr-left__top { margin-bottom: 14px; }
			.gr-left__icon { margin-bottom: 10px; }
			.gr-left__title { font-size: 15px; }
			.gr-right { padding: 20px; }
			.gr-fields { grid-template-columns: 1fr; gap: 12px; }
			.gr-right__head {
				flex-wrap: wrap;
				gap: 10px;
			}
		}

		@media (max-width: 420px) {
			.gr-wrap { font-size: 13px; }
			.gr-left { padding: 16px; }
			.gr-right { padding: 16px; }
			.gr-right__title { font-size: 16px; }
			.gr-field input { font-size: 16px; } /* prevents iOS zoom on focus */
			.gr-dl-all-row { justify-content: stretch; }
			.gr-dl-all {
				width: 100%;
				justify-content: center;
			}
			.gr-file-item {
				flex-wrap: wrap;
				row-gap: 8px;
			}
			.gr-file-name {
				min-width: 0;
				width: 100%;
				order: 1;
				white-space: normal;
				word-break: break-word;
			}
			.gr-dl-btn {
				order: 2;
				margin-left: 44px;
			}
		}
	`;
	document.head.appendChild(style);
};

// ─── Icons ─────────────────────────────────────────────────────────────────────
const ReportIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="22" height="22" aria-hidden="true">
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
		<polyline points="14 2 14 8 20 8"/>
		<line x1="16" y1="13" x2="8" y2="13"/>
		<line x1="16" y1="17" x2="8" y2="17"/>
	</svg>
);

const DownloadIcon = ({ size = 14 }) => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width={size} height={size} aria-hidden="true">
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
		<polyline points="7 10 12 15 17 10"/>
		<line x1="12" y1="15" x2="12" y2="3"/>
	</svg>
);

const LockIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" aria-hidden="true">
		<rect x="4" y="10" width="16" height="10" rx="2"/>
		<path d="M8 10V7a4 4 0 0 1 8 0v3"/>
	</svg>
);

const ExitIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13" aria-hidden="true">
		<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
		<polyline points="16 17 21 12 16 7"/>
		<line x1="21" y1="12" x2="9" y2="12"/>
	</svg>
);

const FileIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
		<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
		<polyline points="14 2 14 8 20 8"/>
	</svg>
);

// ─── Main component ────────────────────────────────────────────────────────────
const GetPatientReportForm = ({ orgId, orgName, hashedId, sessionId, logo }) => {
	const [loading, setLoading] = useState(false);
	const [downloadButtons, setDownloadButtons] = useState([]);
	const [displayFileList, setDisplayFileList] = useState(false);
	const [values, setValues] = useState({ hin: '', dob: '' });
	const [error, setError] = useState({ hin: false, dob: false });

	useEffect(() => {
		injectFont();
		injectStyles();
	}, []);

	const disableButton = () => error.hin || error.dob;

	const handleSubmit = async () => {
		setLoading(true);
		try {
			const fetchFiles = await api(
				apiConfigs.baseOption('post', 'file/retrieveFilesFromSessionId', {
					data: { orgName, hashedId, sessionId, hin: values.hin, dob: values.dob },
				})
			);
			if (fetchFiles && fetchFiles.status === 200) {
				const fileInfoArray = fetchFiles.data.map((fileInfo) => {
					const { fileName, fileType, buffer } = fileInfo;
					const arrayBuffer = new Uint8Array(buffer).buffer;
					const contentType = mime.getType(fileType) || 'application/octet-stream';
					const blob = new Blob([arrayBuffer], { type: contentType });
					return { fileName, url: URL.createObjectURL(blob) };
				});
				setDownloadButtons(fileInfoArray);
				setDisplayFileList(true);
			} else if (fetchFiles.status === 404) {
				alert('User is not valid or not found');
			} else {
				alert('Unable to retrieve report');
			}
		} catch (error) {
			console.error('Error retrieving files:', error);
		} finally {
			setLoading(false);
			setValues({ ...values, hin: '', dob: '' });
		}
	};

	const handleChange = (e) => {
		const { id, value } = e.target;
		if (id === 'hin') {
			if (/^\d*$/.test(value) && value.length <= 10) {
				setValues((prev) => ({ ...prev, hin: value }));
			}
		} else if (id === 'dob') {
			let v = value.replace(/[^0-9]/g, '');
			if (v.length > 4) v = v.slice(0, 4) + '-' + v.slice(4);
			if (v.length > 7) v = v.slice(0, 7) + '-' + v.slice(7, 9);
			setValues((prev) => ({ ...prev, dob: v }));
		}
	};

	const handleDownloadAll = async () => {
		const zip = new JSZip();
		for (const fileInfo of downloadButtons) {
			const response = await fetch(fileInfo.url);
			const data = await response.arrayBuffer();
			zip.file(fileInfo.fileName, data);
		}
		const zipBlob = await zip.generateAsync({ type: 'blob' });
		const downloadLink = document.createElement('a');
		downloadLink.href = URL.createObjectURL(zipBlob);
		downloadLink.download = 'downloaded-files.zip';
		downloadLink.style.display = 'none';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		document.body.removeChild(downloadLink);
	};

	return (
		<div className="gr-wrap">
			<div className="gr-card">

				{/* ── Left orange panel ── */}
				<div className="gr-left">
					<div className="gr-left__top">
						<div className="gr-left__icon">
							<ReportIcon />
						</div>
						<div className="gr-left__title">
							{displayFileList ? 'Your Reports' : 'Get Your Reports'}
						</div>
						<div className="gr-left__sub">
							{displayFileList
								? 'Your files are ready. Download them below.'
								: 'Access your medical files sent by your clinic securely.'
							}
						</div>
					</div>
					<div className="gr-left__bottom">
						<div className="gr-left__note">
							<LockIcon />
							{displayFileList ? 'Links expire in 72 hours' : '256-bit encrypted · PHIPA compliant'}
						</div>
					</div>
				</div>

				{/* ── Right panel ── */}
				<div className="gr-right">
					{!displayFileList ? (
						/* ── Form state ── */
						<>
							<div className="gr-right__label">Verify identity</div>
							<div className="gr-right__title">Get Your Reports</div>
							<div className="gr-right__sub">
								Enter your HIN and date of birth to download your files securely.
							</div>

							<div className="gr-fields">
								<div className="gr-field">
									<label htmlFor="hin">HIN</label>
									<input
										id="hin"
										type="text"
										placeholder="1234567890"
										value={values.hin}
										onChange={handleChange}
										onBlur={() =>
											setError((prev) => ({ ...prev, hin: !/^\d{10}$/.test(values.hin) }))
										}
										className={error.hin ? 'error' : ''}
										maxLength={10}
									/>
									{error.hin
										? <div className="gr-field__error">Must be exactly 10 digits</div>
										: <div className="gr-field__hint">10-digit number</div>
									}
								</div>
								<div className="gr-field">
									<label htmlFor="dob">Date of Birth</label>
									<input
										id="dob"
										type="text"
										placeholder="YYYY-MM-DD"
										value={values.dob}
										onChange={handleChange}
										onBlur={() =>
											setError((prev) => ({ ...prev, dob: !/^\d{4}-\d{2}-\d{2}$/.test(values.dob) }))
										}
										className={error.dob ? 'error' : ''}
									/>
									{error.dob
										? <div className="gr-field__error">Format must be YYYY-MM-DD</div>
										: <div className="gr-field__hint">e.g. 1990-01-15</div>
									}
								</div>
							</div>

							{loading ? (
								<div className="gr-spinner-wrap">
									<CircularProgress style={{ color: '#E47029' }} size={28} />
								</div>
							) : (
								<button
									className="gr-btn"
									disabled={disableButton()}
									onClick={handleSubmit}
								>
									<DownloadIcon size={14} />
									Get Report
								</button>
							)}

							<div className="gr-security">
								<LockIcon />
								256-bit encrypted · PHIPA compliant
							</div>
						</>
					) : (
						/* ── File list state ── */
						<>
							<div className="gr-right__head">
								<div>
									<div className="gr-right__label">Files ready</div>
									<div className="gr-right__title">Your Reports</div>
								</div>
								<button
									className="gr-exit"
									onClick={() => window.location.reload()}
								>
									<ExitIcon />
									Exit
								</button>
							</div>

							<div className="gr-file-note">
								Your clinic has sent you files. Click <b>Download</b> to access each one.
								Links are active for <b>72 hours</b> — download promptly.
							</div>

							{downloadButtons.length > 0 && (
								<div className="gr-dl-all-row">
									<button className="gr-dl-all" onClick={handleDownloadAll}>
										<DownloadIcon size={13} />
										Download All
									</button>
								</div>
							)}

							{downloadButtons.map((fileInfo, index) => (
								<div key={index} className="gr-file-item">
									<div className="gr-file-icon">
										<FileIcon />
									</div>
									<span className="gr-file-name">{fileInfo.fileName}</span>
									<a
										className="gr-dl-btn"
										href={fileInfo.url}
										download
									>
										<DownloadIcon size={12} />
										Download
									</a>
								</div>
							))}
						</>
					)}
				</div>

			</div>
		</div>
	);
};

export default GetPatientReportForm;